import { createContext, useContext, useMemo, type ReactNode } from 'react';

/**
 * Context-Wert: der vom Pinning-Gate (AFO-006) verifizierte CryptoKey.
 *
 * `null` ist der Default außerhalb der Provider-Sichtbarkeit und im
 * Bootstrap-Zwischenzustand; sobald Pinning erfolgreich war, hält der
 * Provider den eigentlichen Key (extractable=false, usages=['wrapKey']).
 *
 * Hinweis: JavaScript kann CryptoKey-Memory nicht aktiv löschen — der Schutz
 * beruht auf `extractable: false` und nicht-persistenter Lebenszeit
 * (AFO-006).
 */
interface PinningContextValue {
  readonly verifiedKey: CryptoKey | null;
}

export const PinningContext = createContext<PinningContextValue>({ verifiedKey: null });

interface PinningProviderProps {
  readonly verifiedKey: CryptoKey | null;
  readonly children: ReactNode;
}

/**
 * Provider, der den verifizierten CryptoKey in den Subtree reicht. Bewusst
 * ohne Memoization-Spielchen: der Eigentümer (Bootstrap-Hook) erzeugt den
 * Value-Objekt-Identifier ein Mal pro Pinning-Lauf, das reicht.
 */
export function PinningProvider(props: PinningProviderProps): JSX.Element {
  const value: PinningContextValue = useMemo(
    () => ({ verifiedKey: props.verifiedKey }),
    [props.verifiedKey],
  );
  return <PinningContext.Provider value={value}>{props.children}</PinningContext.Provider>;
}

/**
 * Lese-Hook für den verifizierten CryptoKey.
 *
 * Wirft, wenn der Key noch nicht vorhanden ist. Aufrufer dürfen den Hook nur
 * im Branch nach erfolgreichem Pinning aufrufen — typische Konsumenten sind
 * Submit-Handler (AFO-009), die den Key synchron einlesen, statt ihn in
 * Closures zu cachen.
 */
export function useVerifiedKey(): CryptoKey {
  const ctx = useContext(PinningContext);
  if (ctx.verifiedKey === null) {
    throw new Error('useVerifiedKey called before pinning succeeded.');
  }
  return ctx.verifiedKey;
}
